import React, { useState, useEffect } from 'react';
import Dialog from '../Dialog/Dialog';
import Button from '../Button/Button';
import Input from '../Input/Input';
import DeleteButton from '../DeleteButton/DeleteButton';
import LanguageDialog from '../LanguageDialog/LanguageDialog';
import ModalList from '../ModalList/ModalList';
import Dropdown from '../Dropdown/Dropdown';
import { TextButton, IconTextButton } from '../Button';
import { useLanguages } from '../../hooks/useLanguages';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useAuth } from '../../context/AuthContext';
import { useProfile } from '../../context/ProfileContext';
import { usePermissions } from '../../lib/permissions';
import { supabase } from '../../lib/supabase';
import { deleteAllTranslations } from '../../services/translationService';
import { useNotification } from '../../context/NotificationContext';
import './EditProjectDialog.css';

interface EditProjectDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (newProjectName: string, placeholderFormat: string) => Promise<void>;
  onDelete: () => void;
  projectName: string;
  placeholderFormat: string;
  isLoading: boolean;
  projectId: string;
}

type SettingsTab = 'general' | 'languages' | 'api';

const EditProjectDialog: React.FC<EditProjectDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  projectName,
  placeholderFormat,
  isLoading,
  projectId
}) => {
  const [name, setName] = useState(projectName);
  const [format, setFormat] = useState(placeholderFormat);
  const [isLanguageDialogOpen, setIsLanguageDialogOpen] = useState(false);
  const [apiKeys, setApiKeys] = useState<Array<{ id: string; key: string; created_at: string }>>([]);
  const [isLoadingApiKeys, setIsLoadingApiKeys] = useState(false);
  const [newApiKey, setNewApiKey] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<SettingsTab>('general');
  const { availableLanguages, addLanguage, deleteLanguage, refreshLanguages } = useLanguages();
  const { selectedProject, selectedTeam } = useWorkspace();
  const { user } = useAuth();
  const { profileData } = useProfile();
  const { permissions } = usePermissions(selectedTeam, user?.id, profileData?.id);
  const { showNotification } = useNotification();
  const [isDeletingTranslations, setIsDeletingTranslations] = useState(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setName(projectName);
      setFormat(placeholderFormat);
      fetchApiKeys();
    }
  }, [isOpen, projectName, placeholderFormat]);

  const fetchApiKeys = async () => {
    try {
      setIsLoadingApiKeys(true);
      const { data, error } = await supabase
        .from('ApiKeys')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setApiKeys(data || []);
    } catch (error) {
      console.error('Error fetching API keys:', error);
    } finally {
      setIsLoadingApiKeys(false);
    }
  };

  const handleSubmit = async () => {
    if (name.trim()) {
      await onSave(name.trim(), format);
    }
  };

  const handleReorderLanguages = async (newOrder: string[]) => {
    try {
      const { error } = await supabase
        .from('Projects')
        .update({ languages: newOrder })
        .eq('id', selectedProject?.id);

      if (error) throw error;
      refreshLanguages();
    } catch (error: any) {
      throw new Error(error.message || 'Failed to reorder languages');
    }
  };

  const handleGenerateApiKey = async () => {
    try {
      setIsLoadingApiKeys(true);
      const { data, error } = await supabase
        .from('ApiKeys')
        .insert([
          {
            project_id: projectId,
            key: crypto.randomUUID()
          }
        ])
        .select()
        .single();

      if (error) throw error;
      setApiKeys([data, ...apiKeys]);
      setNewApiKey(data.key);
    } catch (error) {
      console.error('Error generating API key:', error);
    } finally {
      setIsLoadingApiKeys(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleDeleteApiKey = async (keyId: string) => {
    try {
      setIsLoadingApiKeys(true);
      const { error } = await supabase
        .from('ApiKeys')
        .delete()
        .eq('id', keyId);

      if (error) throw error;
      setApiKeys(apiKeys.filter(key => key.id !== keyId));
    } catch (error) {
      console.error('Error deleting API key:', error);
    } finally {
      setIsLoadingApiKeys(false);
    }
  };

  const formatOptions = [
    {
      id: 'formats',
      items: [
        { id: 'web', label: 'Web {name}', value: 'web' },
        { id: 'unified', label: 'Unified [name]', value: 'unified' },
        { id: 'ios', label: 'iOS %@', value: 'ios' },
        { id: 'android', label: 'Android %1$s', value: 'android' },
        { id: 'lokalise', label: 'Lokalise [%name$s]', value: 'lokalise' }
      ]
    }
  ];

  const headerActions = (
    <div className="dialog-header-actions">
      <Button 
        variant="secondary" 
        onClick={onClose}
        disabled={isLoading}
        size="small"
      >
        Cancel
      </Button>
      <Button 
        variant="primary" 
        onClick={handleSubmit}
        disabled={isLoading || !name.trim()}
        isLoading={isLoading}
        size="small"
      >
        Done
      </Button>
    </div>
  );

  const handleConfirmDeleteTranslations = async () => {
    try {
      setIsDeletingTranslations(true);
      await deleteAllTranslations(projectId);
      showNotification('All translations deleted successfully');
      onClose();
    } catch (error) {
      console.error('Error deleting all translations:', error);
      showNotification('Failed to delete all translations');
    } finally {
      setIsDeletingTranslations(false);
    }
  };

  const renderGeneralContent = () => (
    <>
      <Input
        label="Project Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Enter project name"
        disabled={isLoading}
        required
      />
      
      <div className="format-options">
        <div className="format-selector">
          <div className="option-text">
            <span className="option-title">Placeholder Format</span>
          </div>
          <Dropdown
            items={formatOptions}
            selectedId={format}
            onSelect={(value) => setFormat(value)}
            disabled={isLoading}
            hasSections={true}
            position="right"
          />
        </div>
      </div>

      {/* Danger Zone 1: Delete Project */}
      {permissions.canManageProjects && (
        <div className="danger-zone">
          <div className="section-header">
            <h3 className="section-title">Delete Project</h3>
            <p className="section-description">
              This permanently deletes the '{name}' project, including all its keys, translations, and reviews. This action cannot be undone.
            </p>
          </div>
          <DeleteButton
            onDelete={onDelete}
            disabled={isLoading}
            size="small"
            message={`Delete project '${name}'?`}
          />
        </div>
      )}

      {/* Danger Zone 2: Delete All Translations */}
      <div className="danger-zone">
        <div className="section-header">
          <h3 className="section-title">Delete Project Translations</h3>
          <p className="section-description">
            This permanently deletes all translation keys and strings within the '{name}' project. The project itself and its reviews will remain. This action cannot be undone.
          </p>
        </div>
        <DeleteButton
          onDelete={handleConfirmDeleteTranslations}
          disabled={isLoading || isDeletingTranslations}
          size="small"
          message={`Delete all translations in project '${name}'?`}
        />
      </div>
    </>
  );

  const renderLanguagesContent = () => (
    <>
      <div>
        <span className="project-languages-title">Project Languages</span>
      </div>
      <ModalList 
        items={availableLanguages}
        onDeleteClick={deleteLanguage}
        emptyMessage="No languages added"
        draggable={true}
        onReorder={handleReorderLanguages}
      />
      <Button
        variant="secondary"
        onClick={() => setIsLanguageDialogOpen(true)}
        size="small"
      >
        Add Language
      </Button>
    </>
  );

  const renderApiContent = () => {
    const webPlaceholder = "{0}";
    const codeExample = `{
  "format": "json",
  "placeholderFormat": "web"
}`;
    
    return (
      <div className="api-keys-content">
        <div className="option-text">
          <span className="option-title">API Access</span>
          <span className="option-description">
            Generate API keys to access your translations programmatically.
          </span>
        </div>
        <div className="api-keys-section">
          {apiKeys.length > 0 ? (
            <div className="api-keys-list">
              {apiKeys.map((key) => (
                <div key={key.id} className="api-key-item">
                  <div className="api-key-info">
                    <code className="key-text">{key.key}</code>
                    <span className="api-key-date">
                      Created {new Date(key.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="api-key-actions">
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => copyToClipboard(key.key)}
                    >
                      Copy
                    </Button>
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => handleDeleteApiKey(key.id)}
                      disabled={isLoadingApiKeys}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : null}
          <Button
            variant="secondary"
            size="small"
            onClick={handleGenerateApiKey}
            disabled={isLoadingApiKeys}
          >
            Generate API Key
          </Button>
        </div>

        <div className="api-guide">
          <span className="api-guide-title">How to use the API</span>
          
          <div className="api-guide-section">
            <span className="api-guide-subtitle">Get translations</span>
            <div className="api-guide-code">
              <code>
                curl -X POST https://api.texta.app/functions/v1/pull-translations \<br/>
                &nbsp;&nbsp;-H "x-api-key: YOUR_API_KEY" \<br/>
                &nbsp;&nbsp;-H "Content-Type: application/json" \<br/>
                &nbsp;&nbsp;-d '${codeExample}'
              </code>
            </div>
          </div>

          <div className="api-guide-section">
            <span className="api-guide-subtitle">Parameters</span>
            <div className="api-guide-params">
              <div className="param-item">
                <code>format</code>
                <span>"json" | "strings" | "android"</span>
              </div>
              <div className="param-item">
                <code>placeholderFormat</code>
                <span>"web" | "ios" | "android"</span>
              </div>
              <div className="param-item">
                <code>language</code>
                <span>Optional. Language code like "en" or "da". If not provided, returns all languages as a zip file.</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'general':
        return renderGeneralContent();
      case 'languages':
        return renderLanguagesContent();
      case 'api':
        return renderApiContent();
      default:
        return null;
    }
  };

  return (
    <>
      <Dialog
        isOpen={isOpen}
        onClose={onClose}
        title="Project Settings"
        headerActions={headerActions}
        showCloseButton={false}
        className="edit-project-dialog"
      >
        <div className="edit-project-container">
          <div className="edit-project-sidebar">
            <button
              className={`sidebar-item ${activeTab === 'general' ? 'selected' : ''}`}
              onClick={() => setActiveTab('general')}
            >
              General
            </button>
            <button
              className={`sidebar-item ${activeTab === 'languages' ? 'selected' : ''}`}
              onClick={() => setActiveTab('languages')}
            >
              Languages
            </button>
            {permissions.canManageAPIKeys && (
              <button
                className={`sidebar-item ${activeTab === 'api' ? 'selected' : ''}`}
                onClick={() => setActiveTab('api')}
              >
                API Access
              </button>
            )}
          </div>
          <div className="edit-project-main">
            {renderContent()}
          </div>
        </div>
      </Dialog>

      <LanguageDialog
        isOpen={isLanguageDialogOpen}
        onClose={() => setIsLanguageDialogOpen(false)}
        onSelect={addLanguage}
        existingLanguages={availableLanguages}
        onAdd={addLanguage}
        isLoading={isLoading}
      />
    </>
  );
};

export default EditProjectDialog; 